import { supabase } from "./supabase";
import {
  calculateReferralCommission,
} from "../utils/commissionTiers";
import { formatCurrency } from "../utils/helpers";

// Cache for referral data with 5-minute expiration
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`📋 Cache hit for referral data: ${key}`);
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
  console.log(`💾 Cached referral data: ${key}`);
};

// Clear cache when needed
export const clearReferralCache = () => {
  cache.clear();
  console.log("🧹 Referral cache cleared");
};

// Force clear cache on module load (for development)
if (process.env.NODE_ENV === "development") {
  clearReferralCache();
}

export const referralService = {
  // Create a new referral invitation
  async createInvitation(
    referrerName,
    refereeName,
    referrerEmail = null,
    refereeEmail = null
  ) {
    try {
      const { data, error } = await supabase.rpc("create_referral_invitation", {
        p_referrer_name: referrerName,
        p_referee_name: refereeName,
        p_referrer_email: referrerEmail,
        p_referee_email: refereeEmail,
      });

      if (error) throw error;

      const result = data[0];
      return {
        success: result.success,
        message: result.message,
        invitationId: result.invitation_id,
        invitationCode: result.invitation_code,
      };
    } catch (error) {
      console.error("Error creating referral invitation:", error);
      throw error;
    }
  },

  // Create referral connection directly when user signs up (for invite links)
  async createReferralConnectionOnSignup(
    referrerName,
    refereeName,
    refereeEmail = null
  ) {
    try {
      console.log(
        "📞 Calling database function create_referral_connection_on_signup"
      );
      console.log("  Parameters:", {
        p_referrer_name: referrerName,
        p_referee_name: refereeName,
        p_referee_email: refereeEmail,
      });

      const { data, error } = await supabase.rpc(
        "create_referral_connection_on_signup",
        {
          p_referrer_name: referrerName,
          p_referee_name: refereeName,
          p_referee_email: refereeEmail,
        }
      );

      console.log("  Database response data:", data);
      console.log("  Database response error:", error);

      if (error) {
        console.error("  ❌ Database error:", error);
        throw error;
      }

      console.log("  ✅ Database function succeeded:", data);
      return data;
    } catch (error) {
      console.error("❌ Error in createReferralConnectionOnSignup:", error);
      throw error;
    }
  },

  // Accept a referral invitation
  async acceptInvitation(invitationCode) {
    try {
      const { data, error } = await supabase.rpc("accept_referral_invitation", {
        p_invitation_code: invitationCode,
      });

      if (error) throw error;

      const result = data[0];
      return {
        success: result.success,
        message: result.message,
        referrerName: result.referrer_name,
        refereeName: result.referee_name,
      };
    } catch (error) {
      console.error("Error accepting referral invitation:", error);
      throw error;
    }
  },

  // Get all referrals for an affiliate (who they referred)
  async getReferrals(affiliateName) {
    try {
      const { data, error } = await supabase
        .from("affiliate_referrals")
        .select("*")
        .eq("referrer_name", affiliateName)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error fetching referrals:", error);
      throw error;
    }
  },

  // Get who referred this affiliate
  async getReferrer(affiliateName) {
    try {
      const { data, error } = await supabase
        .from("affiliate_referrals")
        .select("*")
        .eq("referee_name", affiliateName)
        .eq("status", "accepted")
        .single();

      if (error && error.code !== "PGRST116") throw error;
      return data;
    } catch (error) {
      console.error("Error fetching referrer:", error);
      throw error;
    }
  },

  // Get referral earnings for an affiliate
  async getReferralEarnings(affiliateName, startDate = null, endDate = null) {
    try {
      let query = supabase
        .from("affiliate_referral_earnings")
        .select("*")
        .eq("referrer_name", affiliateName)
        .order("period_start", { ascending: false });

      if (startDate) {
        query = query.gte("period_start", startDate);
      }
      if (endDate) {
        query = query.lte("period_end", endDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error("Error fetching referral earnings:", error);
      throw error;
    }
  },

  // Calculate referral earnings for a period
  async calculateReferralEarnings(startDate, endDate) {
    try {
      // Try the fixed function first
      const { data, error } = await supabase.rpc(
        "calculate_referral_earnings",
        {
          p_start_date: startDate,
          p_end_date: endDate,
        }
      );

      if (error) {
        console.log(
          "Fixed function not available, falling back to original:",
          error.message
        );
        // Fallback to original function
        const { data: fallbackData, error: fallbackError } = await supabase.rpc(
          "calculate_referral_earnings",
          {
            p_start_date: startDate,
            p_end_date: endDate,
          }
        );
        if (fallbackError) throw fallbackError;
        const result = fallbackData[0];
        return {
          processedReferrals: result.processed_referrals,
          totalReferralCommission: result.total_referral_commission,
        };
      }

      const result = data[0];
      return {
        processedReferrals: result.processed_referrals,
        totalReferralCommission: result.total_referral_commission,
      };
    } catch (error) {
      console.error("Error calculating referral earnings:", error);
      throw error;
    }
  },

  // Update referral earnings for all weeks in the last 3 months (for payout integration)
  // TODO: deprecated, remove this function
  async updateReferralEarningsForPayouts() {
    try {
      console.log("🔄 Updating referral earnings for payout periods...");

      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3); // Last 3 months

      // Generate weekly periods matching the payout system
      const weeks = [];
      let currentWeekStart = new Date(startDate);

      // Find the Monday of the start week
      while (currentWeekStart.getDay() !== 1) {
        currentWeekStart.setDate(currentWeekStart.getDate() - 1);
      }

      while (currentWeekStart <= endDate) {
        const weekEnd = new Date(currentWeekStart);
        weekEnd.setDate(weekEnd.getDate() + 6); // Sunday

        weeks.push({
          start: new Date(currentWeekStart),
          end: new Date(weekEnd),
        });

        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      }

      console.log(
        `📅 Processing ${weeks.length} weeks for referral earnings...`
      );

      // Calculate referral earnings for each week
      let totalProcessed = 0;
      let totalCommission = 0;

      for (const week of weeks) {
        const result = await this.calculateReferralEarnings(
          week.start.toISOString().split("T")[0],
          week.end.toISOString().split("T")[0]
        );

        totalProcessed += result.processedReferrals;
        totalCommission += result.totalReferralCommission;
      }

      console.log(
        `✅ Updated referral earnings: ${totalProcessed} referrals processed, ${totalCommission.toFixed(2)} total commission`
      );

      return {
        weeksProcessed: weeks.length,
        totalProcessed,
        totalCommission,
      };
    } catch (error) {
      console.error("Error updating referral earnings for payouts:", error);
      throw error;
    }
  },

  // Get referral statistics using optimized database function
  async getReferralStatsOptimized(
    affiliateName,
    startDate = null,
    endDate = null
  ) {
    try {
      console.log(`🚀 Getting optimized referral stats for ${affiliateName}`);

      // Check cache first
      const cacheKey = `referral_stats_optimized_${affiliateName}_${startDate}_${endDate}`;
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) return cachedResult;

      // Use the auto-calculating optimized database function
      const { data, error } = await supabase.rpc(
        "get_referral_summary_optimized_with_auto_calc",
        {
          p_affiliate_name: affiliateName,
          start_date: startDate,
          end_date: endDate,
        }
      );

      if (error) {
        console.error("❌ Database function error:", error);
        throw error;
      }

      const result = data[0];
      if (!result) {
        throw new Error("No data returned from optimized referral function");
      }

      // Parse the referral details JSON
      const referrals = result.referral_details || [];

      const optimizedStats = {
        totalReferrals: result.total_referrals || 0,
        activeReferrals: result.active_referrals || 0,
        pendingReferrals: result.pending_referrals || 0,
        totalReferralEarnings: parseFloat(result.total_referral_earnings || 0),
        currentMonthEarnings: parseFloat(result.current_month_earnings || 0),
        pendingBonusAmount: parseFloat(result.pending_bonus_amount || 0),
        formattedTotalEarnings: formatCurrency(
          parseFloat(result.total_referral_earnings || 0)
        ),
        referrals: referrals,
        lastUpdated: new Date().toISOString(),
        dataSource: "optimized_database_function",
      };

      console.log(`✅ Optimized referral stats loaded:`, {
        affiliateName,
        totalReferrals: optimizedStats.totalReferrals,
        totalEarnings: optimizedStats.totalReferralEarnings,
        activeReferrals: optimizedStats.activeReferrals,
      });

      // Cache the result
      setCachedData(cacheKey, optimizedStats);

      return optimizedStats;
    } catch (error) {
      console.error("❌ Error in getReferralStatsOptimized:", error);

      // Fallback to the original method
      console.log("🔄 Falling back to original getReferralStats method...");
      return await this.getReferralStats(affiliateName);
    }
  },

  // Get referral statistics for an affiliate with proper caching and consistent calculation
  async getReferralStats(affiliateName) {
    try {
      console.log("🔍 Getting optimized referral stats for:", affiliateName);

      // Check cache first
      const cacheKey = `referral_stats_${affiliateName}`;
      const cachedResult = getCachedData(cacheKey);
      if (cachedResult) return cachedResult;

      console.log(`🚀 Loading fresh referral data for ${affiliateName}`);

      // Get actual referral connections (people who signed up through referral links)
      const { data: actualReferrals, error: referralsError } = await supabase
        .from("affiliate_referrals")
        .select("referee_name, status, created_at")
        .eq("referrer_name", affiliateName);

      console.log("📋 Actual referrals result:", {
        actualReferrals,
        referralsError,
      });
      if (referralsError) throw referralsError;

      // Get manual invitations (people you manually invited) - gracefully handle if table doesn't exist
      let invitations = [];
      try {
        const { data: invitationData, error: invitationsError } = await supabase
          .from("affiliate_referral_invitations")
          .select("referee_name, status, created_at, invitation_code")
          .eq("referrer_name", affiliateName);

        if (invitationsError) {
          console.log("⚠️ Invitations table error:", invitationsError.message);
        } else {
          invitations = invitationData || [];
        }
      } catch (err) {
        console.log("⚠️ Invitations table not available:", err.message);
      }

      console.log("📧 Invitations found:", invitations);

      // Get referral earnings using the UNIFIED calculation method
      let totalReferralEarnings = 0;
      let weeklyBonuses = [];

      try {
        // Get traditional referral earnings
        const { data: earningsData, error: earningsError } = await supabase
          .from("affiliate_referral_earnings")
          .select("referral_commission, referee_name, period_start, period_end")
          .eq("referrer_name", affiliateName);

        if (earningsError) {
          console.log("⚠️ Earnings table error:", earningsError.message);
        } else {
          totalReferralEarnings += (earningsData || []).reduce(
            (sum, e) => sum + parseFloat(e.referral_commission || 0),
            0
          );
        }

        // Get weekly referral bonuses (10% of referee commission earnings)
        const { data: bonusData, error: bonusError } = await supabase
          .from("affiliate_referral_bonuses")
          .select("*")
          .eq("referrer_name", affiliateName)
          .order("week_start", { ascending: false });

        if (bonusError) {
          console.log("⚠️ Bonus table error:", bonusError.message);
        } else {
          weeklyBonuses = bonusData || [];
          totalReferralEarnings += weeklyBonuses.reduce(
            (sum, bonus) => sum + parseFloat(bonus.bonus_amount || 0),
            0
          );
        }
      } catch (err) {
        console.log("⚠️ Earnings calculation error:", err.message);
      }

      console.log(
        "💰 Total referral earnings calculated:",
        totalReferralEarnings
      );

      // Combine both types of referrals for display with enhanced earnings data
      const allReferrals = await Promise.all([
        // Actual referral connections (higher priority - these are real signups)
        ...(actualReferrals?.map(async (r) => {
          // Calculate individual referral earnings
          const referralEarnings = weeklyBonuses
            .filter((bonus) => bonus.referee_name === r.referee_name)
            .reduce(
              (sum, bonus) => sum + parseFloat(bonus.bonus_amount || 0),
              0
            );

          return {
            referee_name: r.referee_name,
            status: r.status,
            invited_at: r.created_at,
            type: "connection",
            invitation_code: null,
            earnings: referralEarnings,
            formattedEarnings: formatCurrency(referralEarnings),
          };
        }) || []),
        // Manual invitations (only show if they haven't signed up yet)
        ...(invitations
          ?.filter(
            (inv) =>
              !actualReferrals?.some(
                (ref) => ref.referee_name === inv.referee_name
              )
          )
          .map((inv) => ({
            referee_name: inv.referee_name,
            status: inv.status,
            invited_at: inv.created_at,
            type: "invitation",
            invitation_code: inv.invitation_code,
            earnings: 0,
            formattedEarnings: formatCurrency(0),
          })) || []),
      ]);

      const totalReferrals = allReferrals.length;
      const activeReferrals = allReferrals.filter(
        (r) => r.status === "accepted"
      ).length;
      const pendingReferrals = allReferrals.filter(
        (r) => r.status === "pending"
      ).length;

      const result = {
        totalReferrals,
        activeReferrals,
        pendingReferrals,
        totalReferralEarnings,
        formattedTotalEarnings: formatCurrency(totalReferralEarnings),
        referrals: allReferrals,
        weeklyBonuses,
        lastUpdated: new Date().toISOString(),
      };

      console.log("📊 Final optimized referral stats result:", {
        affiliateName,
        totalReferrals,
        totalReferralEarnings,
        activeReferrals,
      });

      // Cache the result
      setCachedData(cacheKey, result);

      return result;
    } catch (error) {
      console.error("❌ Error fetching referral stats:", error);

      // Return safe fallback data
      return {
        totalReferrals: 0,
        activeReferrals: 0,
        pendingReferrals: 0,
        totalReferralEarnings: 0,
        formattedTotalEarnings: formatCurrency(0),
        referrals: [],
        weeklyBonuses: [],
        lastUpdated: new Date().toISOString(),
        error: error.message,
      };
    }
  },

  // Generate a referral link
  generateReferralLink(invitationCode, baseUrl = window.location.origin) {
    return `${baseUrl}/invite/${invitationCode}`;
  },

  // Validate invitation code format
  isValidInvitationCode(code) {
    return /^[A-Z0-9]{8}$/.test(code);
  },

  // Get weekly referral bonus breakdown for display
  async getWeeklyReferralBonuses(affiliateName, limit = 12) {
    try {
      console.log(`🗓️ Getting weekly referral bonuses for ${affiliateName}`);

      const { data: bonuses, error } = await supabase
        .from("affiliate_referral_bonuses")
        .select(
          "week_start, week_end, referee_name, referee_earnings, bonus_amount, is_paid, created_at"
        )
        .eq("referrer_name", affiliateName)
        .order("week_start", { ascending: false })
        .limit(limit * 10); // Get more records to group properly

      if (error) {
        console.error("❌ Error fetching weekly bonuses:", error);
        throw error;
      }

      // Group by week and aggregate
      const weeklyBreakdown = new Map();

      bonuses?.forEach((bonus) => {
        const weekKey = `${bonus.week_start}_${bonus.week_end}`;

        if (!weeklyBreakdown.has(weekKey)) {
          weeklyBreakdown.set(weekKey, {
            weekStart: bonus.week_start,
            weekEnd: bonus.week_end,
            totalBonus: 0,
            totalRefereeEarnings: 0,
            referees: [],
            isPaid: bonus.is_paid,
            createdAt: bonus.created_at,
          });
        }

        const week = weeklyBreakdown.get(weekKey);
        week.totalBonus += parseFloat(bonus.bonus_amount || 0);
        week.totalRefereeEarnings += parseFloat(bonus.referee_earnings || 0);
        week.referees.push({
          name: bonus.referee_name,
          earnings: parseFloat(bonus.referee_earnings || 0),
          bonus: parseFloat(bonus.bonus_amount || 0),
        });
      });

      // Convert to array and sort by week start (most recent first)
      const weeklyArray = Array.from(weeklyBreakdown.values())
        .sort((a, b) => new Date(b.weekStart) - new Date(a.weekStart))
        .slice(0, limit);

      console.log(`✅ Found ${weeklyArray.length} weeks of bonus data`);

      return weeklyArray;
    } catch (error) {
      console.error("❌ Error in getWeeklyReferralBonuses:", error);
      return [];
    }
  },

  // Calculate referral earnings for weekly payout reports using UNIFIED logic
  async calculateWeeklyReferralEarnings(affiliateName, weeklyEarnings) {
    try {
      console.log(
        `🔢 Calculating weekly referral earnings for ${affiliateName}`
      );

      // Get referrals where this affiliate is the referrer
      const { data: referrals, error: referralsError } = await supabase
        .from("affiliate_referrals")
        .select("referee_name")
        .eq("referrer_name", affiliateName)
        .eq("status", "accepted");

      if (referralsError) throw referralsError;

      if (!referrals || referrals.length === 0) {
        console.log(`ℹ️ No active referrals found for ${affiliateName}`);
        return 0; // No referrals
      }

      console.log(
        `📊 Found ${referrals.length} active referrals for ${affiliateName}`
      );

      // Calculate 10% commission from each referee's commission earnings using UNIFIED logic
      let totalReferralCommission = 0;

      weeklyEarnings.forEach((week) => {
        referrals.forEach((referral) => {
          // Find this referee's earnings in the week
          const refereeEarnings = week.affiliateBreakdown?.find(
            (affiliate) => affiliate.affiliate === referral.referee_name
          );

          if (refereeEarnings && refereeEarnings.totalCommission > 0) {
            // Use the UNIFIED 10% commission rate
            const referralCommission = calculateReferralCommission(
              refereeEarnings.totalCommission
            );
            totalReferralCommission += referralCommission;

            console.log(
              `💰 Referral commission: ${referral.referee_name} earned ${formatCurrency(refereeEarnings.totalCommission)} -> ${formatCurrency(referralCommission)} for ${affiliateName}`
            );
          }
        });
      });

      console.log(
        `✅ Total referral commission for ${affiliateName}: ${formatCurrency(totalReferralCommission)}`
      );
      return totalReferralCommission;
    } catch (error) {
      console.error("❌ Error calculating weekly referral earnings:", error);
      return 0;
    }
  },
};
